import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent,
  SelectItem, SelectGroup, SelectLabel, SelectSeparator,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowClockwise, Upload, DownloadSimple, Trash,
  Warning, ChartLineUp, FileArrowUp, Info,
} from "@phosphor-icons/react";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch { return s; }
};

const dateToIso = (d) => (d ? d.toISOString().slice(0, 10) : "");

// ---------------------------------------------------------------------------
// Period model
// ---------------------------------------------------------------------------
// A "period" is either:
//   { kind: "month", year: 2026, month: 5 }   -> 1-May-26 to 31-May-26
//   { kind: "fy",    fy_start: 2026 }         -> 1-Apr-26 to 31-Mar-27
// Serialised in state as an id string: "M-2026-05" or "FY-2026".
// ---------------------------------------------------------------------------

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Given the meta.period_from/to, return the sorted list of {year, month}
// pairs the file actually covers, plus the FY choices around them.
const buildPeriodOptions = (meta) => {
  const months = [];
  const fys = new Set();
  if (meta?.period_from && meta?.period_to) {
    const from = new Date(meta.period_from + "T00:00:00");
    const to   = new Date(meta.period_to   + "T00:00:00");
    const cur  = new Date(from.getFullYear(), from.getMonth(), 1);
    while (cur <= to) {
      months.push({ year: cur.getFullYear(), month: cur.getMonth() + 1 });
      // FY = April..March; if this month is Jan/Feb/Mar it belongs to previous FY
      const fyStart = cur.getMonth() < 3 ? cur.getFullYear() - 1 : cur.getFullYear();
      fys.add(fyStart);
      cur.setMonth(cur.getMonth() + 1);
    }
  } else {
    // No file uploaded yet — default to current FY only
    const now = new Date();
    const fy = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    fys.add(fy);
  }
  return {
    months, // newest-last
    fys: Array.from(fys).sort((a, b) => a - b),
  };
};

const periodToIds = (period) => {
  if (period.kind === "month") {
    return `M-${period.year}-${String(period.month).padStart(2, "0")}`;
  }
  return `FY-${period.fy_start}`;
};

const idToPeriod = (id) => {
  if (id.startsWith("M-")) {
    const [, y, m] = id.split("-");
    return { kind: "month", year: +y, month: +m };
  }
  if (id.startsWith("FY-")) {
    return { kind: "fy", fy_start: +id.slice(3) };
  }
  return null;
};

const periodLabel = (period) => {
  if (period.kind === "month") {
    return `${MONTH_NAMES[period.month - 1]} ${period.year}`;
  }
  const end = String(period.fy_start + 1).slice(-2);
  return `Year FY ${period.fy_start}-${end}`;
};

const periodRange = (period) => {
  if (period.kind === "month") {
    const from = new Date(period.year, period.month - 1, 1);
    const to   = new Date(period.year, period.month,     0);
    return { from: dateToIso(from), to: dateToIso(to) };
  }
  const from = new Date(period.fy_start,     3,  1);   // 1 Apr
  const to   = new Date(period.fy_start + 1, 2, 31);   // 31 Mar
  return { from: dateToIso(from), to: dateToIso(to) };
};

const defaultPeriod = () => {
  const now = new Date();
  const fy = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  return { kind: "fy", fy_start: fy };
};

const formatSignedINR = (v) => {
  if (v === null || v === undefined || v === 0) return "—";
  const abs = fmtINR(Math.abs(v));
  return v < 0 ? `(${abs})` : abs;
};

export default function ProfitLossTab({ refreshKey }) {
  const [period, setPeriod] = useState(defaultPeriod());
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUnclassified, setShowUnclassified] = useState(false);
  const fileRef = useRef(null);

  // Derive start / end date from the current period selection
  const { from: startDate, to: endDate } = useMemo(() => periodRange(period), [period]);
  const periodOptions = useMemo(() => buildPeriodOptions(meta), [meta]);

  const loadMeta = useCallback(async () => {
    try {
      const r = await api.get("/pnl/meta");
      setMeta(r.data);
      // On first meta load, if the file covers a single FY, default the period
      // to that FY; otherwise leave the user's current pick alone.
      if (r.data?.uploaded && r.data.period_from) {
        const from = new Date(r.data.period_from + "T00:00:00");
        const fyStart = from.getMonth() < 3 ? from.getFullYear() - 1 : from.getFullYear();
        setPeriod((p) => (p.kind === "fy" && !periodTouched.current ? { kind: "fy", fy_start: fyStart } : p));
      }
    } catch { setMeta({ uploaded: false }); }
  }, []);

  // Track whether the user manually changed the period so we don't overwrite it
  const periodTouched = useRef(false);
  const changePeriod = (nextId) => {
    const p = idToPeriod(nextId);
    if (p) {
      periodTouched.current = true;
      setPeriod(p);
    }
  };

  const load = useCallback(async () => {
    if (!meta?.uploaded) return;
    setLoading(true);
    try {
      const r = await api.get("/pnl", {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load P&L");
    } finally { setLoading(false); }
  }, [startDate, endDate, meta?.uploaded]);

  const [buckets, setBuckets] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const loadOverrides = useCallback(async () => {
    try {
      const r = await api.get("/pnl/overrides");
      setOverrides(r.data?.rows || []);
    } catch { setOverrides([]); }
  }, []);
  useEffect(() => {
    api.get("/pnl/buckets")
      .then((r) => setBuckets(r.data?.buckets || []))
      .catch(() => setBuckets([]));
    loadOverrides();
  }, [loadOverrides]);

  const [savingLedger, setSavingLedger] = useState(null);
  const assignBucket = async (ledger, bucket) => {
    setSavingLedger(ledger);
    try {
      await api.post("/pnl/overrides", { ledger, bucket });
      toast.success(`${ledger} → ${buckets.find((b) => b.id === bucket)?.label || bucket}`);
      await Promise.all([load(), loadOverrides()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save override");
    } finally { setSavingLedger(null); }
  };
  const removeOverride = async (ledger) => {
    try {
      await api.delete(`/pnl/overrides/${encodeURIComponent(ledger)}`);
      toast.success(`Reverted override for ${ledger}`);
      await Promise.all([load(), loadOverrides()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove override");
    }
  };

  // ---------- Drill-down (date + cost centre) ----------
  // A drill-down is keyed by a stable string. For a P&L line we use
  // "bucket:<id>"; for an unclassified ledger we use "ledger:<name>".
  const [drillKey, setDrillKey]   = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const openDrill = async (key, params) => {
    if (drillKey === key) {
      setDrillKey(null);
      setDrillData(null);
      return;
    }
    setDrillKey(key);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const r = await api.get("/pnl/txns", {
        params: { start_date: startDate, end_date: endDate, limit: 400, ...params },
      });
      setDrillData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load transactions");
      setDrillKey(null);
    } finally { setDrillLoading(false); }
  };

  useEffect(() => { loadMeta(); }, [loadMeta, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/pnl/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Ingested ${r.data.txn_count} transactions across ${r.data.ledger_count} ledgers (${fmtDate(r.data.period_from)} → ${fmtDate(r.data.period_to)})`);
      // Snap the period to the newly uploaded file's FY
      if (r.data.period_from) {
        const from = new Date(r.data.period_from + "T00:00:00");
        const fyStart = from.getMonth() < 3 ? from.getFullYear() - 1 : from.getFullYear();
        periodTouched.current = false;
        setPeriod({ kind: "fy", fy_start: fyStart });
      }
      await loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onClear = async () => {
    if (!window.confirm("Wipe every Tally P&L transaction? This cannot be undone.")) return;
    try {
      await api.delete("/pnl/clear");
      toast.success("Cleared");
      setData(null);
      await loadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  const onExport = async () => {
    try {
      const url = `/pnl/export?start_date=${startDate}&end_date=${endDate}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PnL_${startDate}_to_${endDate}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const outletCols = data?.outlets || [];

  const summary = useMemo(() => {
    if (!data) return null;
    const findRow = (label) => data.rows.find((r) => r.label === label);
    const rev  = findRow("Net Revenue")?.values?.consolidated || 0;
    const gp   = findRow("Gross Profit")?.values?.consolidated || 0;
    const ebi  = findRow("EBITDA")?.values?.consolidated || 0;
    const pat  = findRow("Profit After Tax (PAT)")?.values?.consolidated || 0;
    return { rev, gp, ebi, pat,
      gpm: rev ? (gp / rev) * 100 : null,
      em:  rev ? (ebi / rev) * 100 : null,
      pm:  rev ? (pat / rev) * 100 : null,
    };
  }, [data]);

  return (
    <div className="space-y-4" data-testid="pnl-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-100 flex items-center gap-2">
            <ChartLineUp size={16} weight="duotone" className="text-cyan-300" />
            Profit &amp; Loss
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Consolidated + outlet-wise P&amp;L computed live from your Tally &ldquo;All Ledger Accounts&rdquo; export.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={onUpload}
            className="hidden"
            data-testid="pnl-file-input"
          />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="h-9 border-cyan-800 text-cyan-100 bg-cyan-950/60 hover:bg-cyan-900/60"
            data-testid="pnl-upload-button">
            <FileArrowUp size={13} weight="bold" className="mr-1.5" />
            {uploading ? "Uploading…" : "Upload Tally alldata"}
          </Button>
          <Button variant="outline" size="sm" onClick={onExport} disabled={!data || loading}
            className="h-9 border-emerald-700 text-emerald-200 bg-slate-900 hover:bg-emerald-900/30"
            data-testid="pnl-export-button">
            <DownloadSimple size={13} weight="bold" className="mr-1" />Export XLSX
          </Button>
          <Button variant="outline" size="sm" onClick={load}
            className="h-9 border-slate-700 text-slate-300 bg-slate-900 hover:bg-slate-800"
            data-testid="pnl-refresh">
            <ArrowClockwise size={13} weight="bold" className="mr-1" />Refresh
          </Button>
        </div>
      </div>

      {/* Upload meta */}
      {meta?.uploaded && (
        <div className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-300"
             data-testid="pnl-meta">
          <span><span className="text-slate-500">Source:</span> <span className="font-mono">{meta.filename}</span></span>
          <span><span className="text-slate-500">Ledgers:</span> {meta.ledger_count}</span>
          <span><span className="text-slate-500">Txns:</span> {meta.txn_count.toLocaleString("en-IN")}</span>
          <span><span className="text-slate-500">Period:</span> {fmtDate(meta.period_from)} → {fmtDate(meta.period_to)}</span>
          <span className="text-slate-600">•</span>
          <span className="text-slate-500">Uploaded {fmtDate(meta.uploaded_at)} by {meta.uploaded_by}</span>
          <button onClick={onClear}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-800/60 bg-rose-950/40 text-rose-300 hover:bg-rose-900/60 text-[10px]"
            title="Wipe all P&L data" data-testid="pnl-clear-button">
            <Trash size={11} weight="bold" />Clear
          </button>
        </div>
      )}

      {/* Period selector */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3 flex flex-wrap items-end gap-3" data-testid="pnl-period-controls">
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
            Report Period
          </label>
          <Select value={periodToIds(period)} onValueChange={changePeriod}>
            <SelectTrigger
              className="h-9 w-64 bg-slate-950 border-slate-700 text-slate-100 text-[12px] font-semibold"
              data-testid="pnl-period-trigger"
            >
              <SelectValue>{periodLabel(period)}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-700 text-slate-100 max-h-[380px]"
              data-testid="pnl-period-content">
              {periodOptions.fys.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-cyan-300">Financial Year</SelectLabel>
                  {periodOptions.fys.map((fy) => {
                    const end = String(fy + 1).slice(-2);
                    const id = periodToIds({ kind: "fy", fy_start: fy });
                    return (
                      <SelectItem key={id} value={id} className="focus:bg-slate-800 focus:text-cyan-100"
                        data-testid={`pnl-period-${id}`}>
                        Year FY {fy}-{end}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              )}
              {periodOptions.months.length > 0 && (
                <>
                  <SelectSeparator className="bg-slate-800" />
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-cyan-300">Month</SelectLabel>
                    {periodOptions.months.map((m) => {
                      const id = periodToIds({ kind: "month", ...m });
                      return (
                        <SelectItem key={id} value={id} className="focus:bg-slate-800 focus:text-cyan-100"
                          data-testid={`pnl-period-${id}`}>
                          {MONTH_NAMES[m.month - 1]} {m.year}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </>
              )}
            </SelectContent>
          </Select>
          <div className="mt-1 text-[10px] text-slate-500 font-mono">
            {fmtDate(startDate)} → {fmtDate(endDate)}
          </div>
        </div>
        {data && (
          <div className="ml-auto text-[10px] uppercase tracking-wider text-slate-500">
            {data.txn_count.toLocaleString("en-IN")} txns · {data.outlets.length} outlets
          </div>
        )}
      </div>

      {/* Empty state */}
      {!meta?.uploaded && (
        <div className="rounded-md border border-dashed border-slate-700 bg-slate-950/60 py-14 flex flex-col items-center gap-3 text-slate-400" data-testid="pnl-empty-state">
          <Upload size={40} weight="duotone" className="text-cyan-500/70" />
          <div className="text-sm font-semibold text-slate-200">No Tally P&amp;L data uploaded yet</div>
          <div className="text-[11px] text-slate-500 max-w-md text-center">
            Export &ldquo;All Ledger Accounts&rdquo; from Tally as XLSX/XLS and upload it above. The system will parse ledger-wise transactions, allocate cost by outlet, and compute a monthly consolidated + outlet-wise P&amp;L.
          </div>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
            className="border-cyan-700 text-cyan-200 bg-cyan-950/60 hover:bg-cyan-900/60"
            data-testid="pnl-empty-upload">
            <FileArrowUp size={13} weight="bold" className="mr-1.5" />Upload Tally alldata
          </Button>
        </div>
      )}

      {/* KPI cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pnl-kpis">
          <KpiCard label="Net Revenue"   value={summary.rev} pct={null}         accent="cyan"    />
          <KpiCard label="Gross Profit"  value={summary.gp}  pct={summary.gpm}  accent="emerald" />
          <KpiCard label="EBITDA"        value={summary.ebi} pct={summary.em}   accent={summary.ebi > 0 ? "emerald" : "rose"} />
          <KpiCard label="PAT"           value={summary.pat} pct={summary.pm}   accent={summary.pat > 0 ? "emerald" : "rose"} />
        </div>
      )}

      {/* P&L table */}
      {meta?.uploaded && (
        <div className="rounded-md border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
            <table className="w-full text-[11px]" data-testid="pnl-table">
              <thead className="bg-slate-900 sticky top-0 z-10">
                <tr className="text-[10px] uppercase tracking-wider font-bold text-cyan-200">
                  <th className="text-left px-3 py-2 min-w-[220px]">Line</th>
                  <th className="text-right px-3 py-2 min-w-[130px] bg-slate-800/80 text-cyan-100">Consolidated</th>
                  {outletCols.map((o) => (
                    <th key={o.key} className="text-right px-3 py-2 min-w-[130px] whitespace-nowrap">
                      {o.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={2 + outletCols.length} className="text-center py-12 text-slate-500">Loading…</td></tr>
                )}
                {!loading && data?.rows?.map((r, i) => {
                  if (r.kind === "section") {
                    return (
                      <tr key={i} className="bg-slate-900/70 border-t border-slate-800" data-testid={`pnl-row-section-${i}`}>
                        <td colSpan={2 + outletCols.length} className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                          {r.label}
                        </td>
                      </tr>
                    );
                  }
                  const isTotal = r.kind === "subtotal" || r.kind === "computed";
                  const rowTone = isTotal
                    ? "bg-slate-900/40 border-t-2 border-cyan-800/60 font-bold text-cyan-100"
                    : "border-t border-slate-800 hover:bg-slate-900/40 text-slate-300";
                  const clickable = r.kind === "line" && r.bucket;
                  const dkey = clickable ? `bucket:${r.bucket}` : null;
                  const isOpen = clickable && drillKey === dkey;
                  return (
                    <React.Fragment key={i}>
                      <tr
                        className={`${rowTone} ${clickable ? "cursor-pointer" : ""}`}
                        onClick={clickable ? () => openDrill(dkey, { bucket: r.bucket }) : undefined}
                        data-testid={`pnl-row-${r.label}`}
                      >
                        <td className="px-3 py-1.5 pl-6">
                          {clickable && (
                            <span className="inline-block w-3 text-slate-500 text-[10px]">{isOpen ? "▾" : "▸"}</span>
                          )}
                          <span className={isTotal ? "font-bold" : ""}>{r.label}</span>
                          {r.data_required && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-950 text-amber-300 border border-amber-700"
                              title={r.missing?.join("; ")}>
                              DATA REQUIRED
                            </span>
                          )}
                        </td>
                        <td className={`px-3 py-1.5 text-right font-mono tabular-nums bg-slate-900/40 ${isTotal ? "text-cyan-100 font-bold" : "text-slate-100"}`}>
                          {formatSignedINR(r.values?.consolidated)}
                        </td>
                        {outletCols.map((o) => (
                          <td key={o.key} className={`px-3 py-1.5 text-right font-mono tabular-nums ${isTotal ? "font-bold" : "text-slate-400"}`}>
                            {formatSignedINR(r.values?.[o.key])}
                          </td>
                        ))}
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-950/80 border-t border-cyan-900/40" data-testid={`pnl-drill-${r.bucket}`}>
                          <td colSpan={2 + outletCols.length} className="p-0">
                            <DrillDown loading={drillLoading} data={drillData} kind="bucket" />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unclassified ledgers */}
      {data?.unclassified?.length > 0 && (
        <div className="rounded-md border border-amber-800/60 bg-amber-950/20 overflow-hidden" data-testid="pnl-unclassified">
          <button onClick={() => setShowUnclassified((v) => !v)}
            className="w-full px-3 py-2 flex items-center justify-between text-[11px] uppercase tracking-wider font-bold text-amber-200 hover:bg-amber-950/40"
            data-testid="pnl-unclassified-toggle">
            <span className="flex items-center gap-1.5">
              <Warning size={13} weight="duotone" />
              {data.unclassified.length} unclassified ledgers  ·  need review to be included in P&amp;L
            </span>
            <span className="text-[10px] normal-case">{showUnclassified ? "hide" : "show"}</span>
          </button>
          {showUnclassified && (
            <div className="max-h-80 overflow-y-auto border-t border-amber-800/40">
              <table className="w-full text-[11px] font-mono">
                <thead className="bg-amber-950/40 sticky top-0">
                  <tr className="text-[10px] uppercase tracking-wider text-amber-300/80">
                    <th className="text-left px-3 py-1.5">Ledger</th>
                    <th className="text-right px-3 py-1.5 w-32">Net (₹)</th>
                    <th className="text-left px-3 py-1.5 w-64">Assign to bucket…</th>
                  </tr>
                </thead>
                <tbody>
                  {data.unclassified.map((u, i) => {
                    const dkey = `ledger:${u.ledger}`;
                    const isOpen = drillKey === dkey;
                    return (
                      <React.Fragment key={i}>
                        <tr
                          className="border-t border-amber-900/30 hover:bg-amber-950/20 cursor-pointer"
                          onClick={() => openDrill(dkey, { ledger: u.ledger })}
                          data-testid={`pnl-unclassified-row-${i}`}
                        >
                          <td className="px-3 py-1 text-slate-200">
                            <span className="inline-block w-3 text-slate-500 text-[10px]">{isOpen ? "▾" : "▸"}</span>
                            {u.ledger}
                          </td>
                          <td className={`px-3 py-1 text-right tabular-nums ${u.net < 0 ? "text-rose-300" : "text-slate-300"}`}>
                            {formatSignedINR(u.net)}
                          </td>
                          <td className="px-3 py-1" onClick={(e) => e.stopPropagation()}>
                            <Select
                              disabled={savingLedger === u.ledger}
                              value=""
                              onValueChange={(bucket) => assignBucket(u.ledger, bucket)}
                            >
                              <SelectTrigger
                                className="h-7 w-60 bg-slate-950 border-amber-800/40 text-[11px] font-sans text-amber-100"
                                data-testid={`pnl-unclassified-assign-${i}`}
                              >
                                <SelectValue placeholder={savingLedger === u.ledger ? "Saving…" : "Pick a bucket…"} />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-950 border-slate-700 text-slate-100 max-h-[320px]">
                                {buckets.map((b) => (
                                  <SelectItem key={b.id} value={b.id} className="focus:bg-slate-800 focus:text-cyan-100">
                                    {b.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-950/90 border-t border-amber-800/30" data-testid={`pnl-unclassified-drill-${i}`}>
                            <td colSpan={3} className="p-0">
                              <DrillDown loading={drillLoading} data={drillData} kind="ledger" />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Manual overrides — user-assigned classifications survive re-uploads */}
      {overrides.length > 0 && (
        <div className="rounded-md border border-cyan-800/50 bg-cyan-950/10 overflow-hidden" data-testid="pnl-overrides-panel">
          <div className="px-3 py-2 flex items-center justify-between border-b border-cyan-800/40">
            <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-200">
              {overrides.length} manual override{overrides.length === 1 ? "" : "s"}
            </div>
            <span className="text-[10px] text-cyan-500/80">Persist across re-uploads. Click × to revert.</span>
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-[11px]">
              <tbody>
                {overrides.map((o) => (
                  <tr key={o.ledger} className="border-b border-cyan-900/20 hover:bg-cyan-950/20" data-testid={`pnl-override-${o.ledger}`}>
                    <td className="px-3 py-1 font-mono text-slate-200">{o.ledger}</td>
                    <td className="px-3 py-1 text-cyan-300">→ {buckets.find((b) => b.id === o.bucket)?.label || o.bucket}</td>
                    <td className="px-3 py-1 text-[9px] text-slate-500 text-right">by {o.updated_by || "—"}</td>
                    <td className="px-3 py-1 text-right w-8">
                      <button
                        onClick={() => removeOverride(o.ledger)}
                        className="text-slate-500 hover:text-rose-300"
                        title="Revert to keyword classifier"
                        data-testid={`pnl-override-revert-${o.ledger}`}
                      >
                        <Trash size={11} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info footer */}
      {meta?.uploaded && (
        <div className="rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2 text-[10px] text-slate-500 flex items-start gap-1.5" data-testid="pnl-info-footer">
          <Info size={12} weight="bold" className="mt-0.5 shrink-0" />
          <span>
            P&amp;L is computed live from the uploaded Tally export. Ledgers are matched to buckets via keyword rules — anything the classifier isn&rsquo;t confident about lands in the amber &ldquo;Unclassified&rdquo; list above so you can review and re-upload with a refined mapping.
          </span>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, pct, accent }) {
  const tones = {
    cyan:    "border-cyan-800    bg-cyan-950/40    text-cyan-100",
    emerald: "border-emerald-800 bg-emerald-950/40 text-emerald-100",
    rose:    "border-rose-800    bg-rose-950/40    text-rose-100",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[accent]}`} data-testid={`pnl-kpi-${label}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="mt-1 font-mono text-2xl font-bold tabular-nums">{formatSignedINR(value)}</div>
      {pct !== null && pct !== undefined && (
        <div className="text-[10px] opacity-70 mt-0.5">{pct.toFixed(1)}% of Net Revenue</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drill-down: shows txns behind a P&L line (grouped by ledger) or an
// unclassified ledger (flat list, since only one ledger by definition).
// ---------------------------------------------------------------------------
function DrillDown({ loading, data, kind }) {
  if (loading) {
    return <div className="px-6 py-4 text-[11px] text-slate-500">Loading transactions…</div>;
  }
  if (!data) return null;
  const txns = data.txns || [];
  const grouped = data.grouped || [];
  return (
    <div className="px-6 py-3 space-y-3">
      {kind === "bucket" && grouped.length > 1 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-300 mb-1.5">
            Ledgers contributing to this line ({grouped.length})
          </div>
          <div className="max-h-40 overflow-y-auto border border-slate-800 rounded">
            <table className="w-full text-[11px] font-mono">
              <thead className="bg-slate-900/70 sticky top-0">
                <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="text-left px-3 py-1">Ledger</th>
                  <th className="text-right px-3 py-1 w-20">Txns</th>
                  <th className="text-right px-3 py-1 w-32">Net (₹)</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map((g) => (
                  <tr key={g.ledger} className="border-t border-slate-800/60">
                    <td className="px-3 py-1 text-slate-200">{g.ledger}</td>
                    <td className="px-3 py-1 text-right text-slate-400">{g.count}</td>
                    <td className={`px-3 py-1 text-right tabular-nums ${g.net < 0 ? "text-rose-300" : "text-slate-100"}`}>
                      {g.net.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-300 mb-1.5 flex items-center justify-between">
          <span>Transactions ({data.total.toLocaleString("en-IN")})</span>
          {txns.length < data.total && (
            <span className="text-[9px] normal-case tracking-normal text-slate-500">
              showing top {txns.length}
            </span>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto border border-slate-800 rounded">
          <table className="w-full text-[11px] font-mono">
            <thead className="bg-slate-900/70 sticky top-0">
              <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                <th className="text-left px-3 py-1 w-20">Date</th>
                {kind === "bucket" && <th className="text-left px-3 py-1">Ledger</th>}
                <th className="text-left px-3 py-1 w-32">Cost Centre</th>
                <th className="text-left px-3 py-1 w-24">Vch Type</th>
                <th className="text-left px-3 py-1 w-16">Vch #</th>
                <th className="text-left px-3 py-1">Contra</th>
                <th className="text-right px-3 py-1 w-24">Debit</th>
                <th className="text-right px-3 py-1 w-24">Credit</th>
              </tr>
            </thead>
            <tbody>
              {txns.length === 0 && (
                <tr><td colSpan={kind === "bucket" ? 8 : 7} className="text-center py-3 text-slate-500">
                  No transactions in the selected window.
                </td></tr>
              )}
              {txns.map((t, i) => (
                <tr key={i} className="border-t border-slate-800/60 hover:bg-slate-900/60">
                  <td className="px-3 py-1 text-slate-300">{t.date}</td>
                  {kind === "bucket" && (
                    <td className="px-3 py-1 text-slate-200 truncate max-w-[260px]" title={t.ledger}>{t.ledger}</td>
                  )}
                  <td className="px-3 py-1 text-cyan-300 truncate max-w-[180px]" title={t.outlet || ""}>{t.outlet || "—"}</td>
                  <td className="px-3 py-1 text-slate-400">{t.vch_type || ""}</td>
                  <td className="px-3 py-1 text-slate-400">{t.vch_no  || ""}</td>
                  <td className="px-3 py-1 text-slate-400 truncate max-w-[280px]" title={t.contra || ""}>{t.contra || ""}</td>
                  <td className="px-3 py-1 text-right tabular-nums text-rose-300">
                    {t.debit  ? t.debit.toLocaleString("en-IN", { maximumFractionDigits: 2 })  : "—"}
                  </td>
                  <td className="px-3 py-1 text-right tabular-nums text-emerald-300">
                    {t.credit ? t.credit.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
